import React, { useState, useMemo } from 'react';
import { Student } from '../types';
import Card from './ui/Card';
import Button from './ui/Button';
import Input from './ui/Input';
import Modal from './ui/Modal';
import {
    GraduationCap,
    Search,
    Edit,
    Eye,
    User,
    ChevronLeft,
    ChevronRight,
    Mail,
    Phone,
    Calendar,
    Plus,
    ToggleLeft,
    ToggleRight,
    BookOpen,
    MapPin
} from 'lucide-react';

interface StudentViewProps {
    students: Student[];
    onEdit: (student: Student) => void;
    onToggleStatus: (studentId: string, newStatus: 'active' | 'inactive', reason?: string) => void;
    onAddStudent?: () => void;
    loading?: boolean;
}

const StudentView: React.FC<StudentViewProps> = ({ students, onEdit, onToggleStatus, onAddStudent, loading = false }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState<string>('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [viewMode, setViewMode] = useState<'table' | 'cards'>('table');
    const [viewingStudent, setViewingStudent] = useState<Student | null>(null);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
    const [statusReason, setStatusReason] = useState('');
    const [showImageModal, setShowImageModal] = useState(false);
    const [selectedImageUrl, setSelectedImageUrl] = useState('');
    const [selectedImageAlt, setSelectedImageAlt] = useState('');

    // Filter and search students
    const filteredStudents = useMemo(() => {
        return students.filter(student => {
            const matchesSearch =
                student.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                student.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                student.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                student.studentId.toLowerCase().includes(searchTerm.toLowerCase()) ||
                student.academicInfo.currentGrade.toLowerCase().includes(searchTerm.toLowerCase()) ||
                student.academicInfo.section.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesStatus = filterStatus === 'all' || student.academicInfo.status === filterStatus;

            return matchesSearch && matchesStatus;
        });
    }, [students, searchTerm, filterStatus]);

    // Pagination
    const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedStudents = filteredStudents.slice(startIndex, startIndex + itemsPerPage);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handleItemsPerPageChange = (value: string) => {
        setItemsPerPage(parseInt(value));
        setCurrentPage(1);
    };

    const getStatusBadgeColor = (status: string) => {
        switch (status) {
            case 'active': return 'bg-green-100 text-green-800';
            case 'inactive': return 'bg-yellow-100 text-yellow-800';
            case 'graduated': return 'bg-blue-100 text-blue-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const handleStatusToggle = (student: Student) => {
        setSelectedStudent(student);
        setStatusReason('');
        setShowStatusModal(true);
    };

    const confirmStatusChange = () => {
        if (!selectedStudent) return;

        const newStatus = selectedStudent.academicInfo.status === 'active' ? 'inactive' : 'active';
        onToggleStatus(selectedStudent.id, newStatus, statusReason);

        setShowStatusModal(false);
        setSelectedStudent(null);
        setStatusReason('');
    };

    const handleImageClick = (imageUrl: string, studentName: string) => {
        setSelectedImageUrl(imageUrl);
        setSelectedImageAlt(`${studentName} - Profile Image`);
        setShowImageModal(true);
    };

    const StudentCard: React.FC<{ student: Student }> = ({ student }) => (
        <Card glassy={true} className="islamic-pattern group hover:scale-105 transition-transform duration-300">
            <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                        <div
                            className="w-12 h-12 rounded-full overflow-hidden bg-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => student.profileImage && handleImageClick(student.profileImage, `${student.firstName} ${student.lastName}`)}
                        >
                            {student.profileImage ? (
                                <img
                                    src={student.profileImage}
                                    alt={`${student.firstName} ${student.lastName}`}
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <div className="w-full h-full bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full flex items-center justify-center">
                                    <GraduationCap className="h-6 w-6 text-white" />
                                </div>
                            )}
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                                {student.firstName} {student.lastName}
                            </h3>
                            <p className="text-sm text-gray-600">{student.studentId}</p>
                        </div>
                    </div>
                    <div className="flex space-x-2">
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setViewingStudent(student)}
                        >
                            <Eye className="h-4 w-4" />
                        </Button>
                        <button
                            onClick={() => handleStatusToggle(student)}
                            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                            title={`Switch to ${student.academicInfo.status === 'active' ? 'inactive' : 'active'}`}
                        >
                            {student.academicInfo.status === 'active' ?
                                <ToggleRight className="h-5 w-5 text-green-600" /> :
                                <ToggleLeft className="h-5 w-5 text-gray-400" />
                            }
                        </button>
                    </div>
                </div>

                <div className="space-y-3 mb-4">
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Mail className="h-4 w-4" />
                        <span>{student.email}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Phone className="h-4 w-4" />
                        <span>{student.phone}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <BookOpen className="h-4 w-4" />
                        <span>Grade {student.academicInfo.currentGrade} - {student.academicInfo.section}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Calendar className="h-4 w-4" />
                        <span>Roll: {student.academicInfo.rollNumber}</span>
                    </div>
                </div>

                <div className="flex items-center justify-between">
                    <div className="flex space-x-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(student.academicInfo.status)}`}>
                            {student.academicInfo.status}
                        </span>
                    </div>
                </div>
            </div>
        </Card>
    );

    const StudentTableRow: React.FC<{ student: Student }> = ({ student }) => (
        <tr className="hover:bg-gray-50 transition-colors duration-200">
            <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center space-x-3">
                    <div
                        className="w-8 h-8 rounded-full overflow-hidden bg-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => student.profileImage && handleImageClick(student.profileImage, `${student.firstName} ${student.lastName}`)}
                    >
                        {student.profileImage ? (
                            <img
                                src={student.profileImage}
                                alt={`${student.firstName} ${student.lastName}`}
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <div className="w-full h-full bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full flex items-center justify-center">
                                <GraduationCap className="h-4 w-4 text-white" />
                            </div>
                        )}
                    </div>
                    <div>
                        <div className="text-sm font-medium text-gray-900">
                            {student.firstName} {student.lastName}
                        </div>
                        <div className="text-sm text-gray-500">{student.studentId}</div>
                    </div>
                </div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{student.email}</div>
                <div className="text-sm text-gray-500">{student.phone}</div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">Grade {student.academicInfo.currentGrade}</div>
                <div className="text-sm text-gray-500">{student.academicInfo.section} - {student.academicInfo.rollNumber}</div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{student.parentInfo.fatherName}</div>
                <div className="text-sm text-gray-500">{student.parentInfo.fatherPhone}</div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(student.academicInfo.status)}`}>
                    {student.academicInfo.status}
                </span>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {new Date(student.academicInfo.admissionDate).toLocaleDateString()}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex items-center space-x-2">
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setViewingStudent(student)}
                    >
                        <Eye className="h-4 w-4" />
                    </Button>
                    <button
                        onClick={() => handleStatusToggle(student)}
                        className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                        title={`Switch to ${student.academicInfo.status === 'active' ? 'inactive' : 'active'}`}
                    >
                        {student.academicInfo.status === 'active' ?
                            <ToggleRight className="h-5 w-5 text-green-600" /> :
                            <ToggleLeft className="h-5 w-5 text-gray-400" />
                        }
                    </button>
                </div>
            </td>
        </tr>
    );

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 shadow-lg">
                        <GraduationCap className="h-6 w-6 text-white" />
                    </div>
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Student Management</h2>
                        <p className="text-gray-600">Manage your school students and their academic records</p>
                    </div>
                </div>
                <div className="flex items-center space-x-3">
                    {onAddStudent && (
                        <Button onClick={onAddStudent} className="flex items-center space-x-2">
                            <Plus className="h-4 w-4" />
                            <span>Add Student</span>
                        </Button>
                    )}
                    <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-600">View:</span>
                        <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                            <button
                                onClick={() => setViewMode('table')}
                                className={`px-3 py-1 text-sm font-medium transition-colors ${viewMode === 'table'
                                    ? 'bg-emerald-500 text-white'
                                    : 'bg-white text-gray-700 hover:bg-gray-50'
                                    }`}
                            >
                                Table
                            </button>
                            <button
                                onClick={() => setViewMode('cards')}
                                className={`px-3 py-1 text-sm font-medium transition-colors ${viewMode === 'cards'
                                    ? 'bg-emerald-500 text-white'
                                    : 'bg-white text-gray-700 hover:bg-gray-50'
                                    }`}
                            >
                                Cards
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <Card glassy={true} className="islamic-pattern">
                <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="md:col-span-2">
                            <Input
                                label="Search Students"
                                placeholder="Search by name, email, student ID, grade, or section..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                icon={<Search className="h-4 w-4" />}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Status</label>
                            <select
                                value={filterStatus}
                                onChange={(e) => setFilterStatus(e.target.value)}
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                            >
                                <option value="all">All Status</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                                <option value="graduated">Graduated</option>
                            </select>
                        </div>
                    </div>
                </div>
            </Card>

            {/* Results Count */}
            <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                    Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredStudents.length)} of {filteredStudents.length} students
                </p>
                <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">Items per page:</span>
                    <select
                        value={itemsPerPage}
                        onChange={(e) => handleItemsPerPageChange(e.target.value)}
                        className="px-2 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                        <option value={10}>10</option>
                        <option value={15}>15</option>
                        <option value={20}>20</option>
                    </select>
                </div>
            </div>

            {/* Students List */}
            {loading ? (
                <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
                </div>
            ) : viewMode === 'table' ? (
                // Table View
                <Card glassy={true} className="islamic-pattern overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Student
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Contact
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Academic Info
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Parent
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Admission Date
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {paginatedStudents.map((student) => (
                                    <StudentTableRow key={student.id} student={student} />
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            ) : (
                // Cards View
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {paginatedStudents.map((student) => (
                        <StudentCard key={student.id} student={student} />
                    ))}
                </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
                <Card glassy={true} className="islamic-pattern">
                    <div className="p-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                                <span className="text-sm text-gray-700">
                                    Page {currentPage} of {totalPages}
                                </span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                </Button>

                                {/* Page Numbers */}
                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                    let pageNum: number;
                                    if (totalPages <= 5) {
                                        pageNum = i + 1;
                                    } else if (currentPage <= 3) {
                                        pageNum = i + 1;
                                    } else if (currentPage >= totalPages - 2) {
                                        pageNum = totalPages - 4 + i;
                                    } else {
                                        pageNum = currentPage - 2 + i;
                                    }

                                    return (
                                        <Button
                                            key={pageNum}
                                            variant={currentPage === pageNum ? "primary" : "secondary"}
                                            size="sm"
                                            onClick={() => handlePageChange(pageNum)}
                                        >
                                            {pageNum}
                                        </Button>
                                    );
                                })}

                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    </div>
                </Card>
            )}

            {/* Status Change Modal */}
            {showStatusModal && selectedStudent && (
                <Modal
                    isOpen={true}
                    onClose={() => {
                        setShowStatusModal(false);
                        setSelectedStudent(null);
                        setStatusReason('');
                    }}
                    title={`Change Status - ${selectedStudent.firstName} ${selectedStudent.lastName}`}
                    size="md"
                >
                    <div className="space-y-6">
                        <div className="text-center">
                            <h3 className="text-lg font-medium text-gray-900 mb-2">
                                Change Student Status
                            </h3>
                            <p className="text-sm text-gray-500">
                                Current Status: <span className="font-medium">{selectedStudent.academicInfo.status}</span>
                            </p>
                            <p className="text-sm text-gray-500">
                                New Status: <span className="font-medium">{selectedStudent.academicInfo.status === 'active' ? 'Inactive' : 'Active'}</span>
                            </p>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Reason for Status Change
                            </label>
                            <textarea
                                value={statusReason}
                                onChange={(e) => setStatusReason(e.target.value)}
                                placeholder="Enter reason for status change..."
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                rows={4}
                            />
                        </div>

                        <div className="flex justify-end space-x-3 pt-4">
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setShowStatusModal(false);
                                    setSelectedStudent(null);
                                    setStatusReason('');
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={confirmStatusChange}
                                disabled={!statusReason.trim()}
                            >
                                Confirm Change
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Read-only View Modal */}
            {viewingStudent && (
                <Modal
                    isOpen={true}
                    onClose={() => setViewingStudent(null)}
                    title={`View Student - ${viewingStudent.firstName} ${viewingStudent.lastName}`}
                    size="full"
                >
                    <div className="space-y-8">
                        {/* Basic Information */}
                        <Card className="p-6">
                            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                                <User className="h-6 w-6 mr-3 text-emerald-600" />
                                Basic Information
                            </h3>

                            {/* Profile Image */}
                            <div className="flex justify-center mb-6">
                                <div
                                    className="w-24 h-24 rounded-full overflow-hidden bg-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                                    onClick={() => viewingStudent.profileImage && handleImageClick(viewingStudent.profileImage, `${viewingStudent.firstName} ${viewingStudent.lastName}`)}
                                >
                                    {viewingStudent.profileImage ? (
                                        <img
                                            src={viewingStudent.profileImage}
                                            alt={`${viewingStudent.firstName} ${viewingStudent.lastName}`}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full flex items-center justify-center">
                                            <GraduationCap className="h-12 w-12 text-white" />
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Student ID</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStudent.studentId}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStudent.firstName}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStudent.lastName}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStudent.email}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStudent.phone}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {new Date(viewingStudent.dateOfBirth).toLocaleDateString()}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900 capitalize">
                                        {viewingStudent.gender}
                                    </div>
                                </div>
                            </div>
                        </Card>

                        {/* Academic Information */}
                        <Card className="p-6">
                            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                                <BookOpen className="h-6 w-6 mr-3 text-emerald-600" />
                                Academic Information
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Current Grade</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStudent.academicInfo.currentGrade}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Section</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStudent.academicInfo.section}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Roll Number</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStudent.academicInfo.rollNumber}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Admission Date</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {new Date(viewingStudent.academicInfo.admissionDate).toLocaleDateString()}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900 capitalize">
                                        {viewingStudent.academicInfo.status}
                                    </div>
                                </div>
                            </div>

                            {/* Grade History */}
                            {viewingStudent.academicInfo.gradeHistory.length > 0 && (
                                <div className="mt-6">
                                    <label className="block text-sm font-medium text-gray-700 mb-3">Grade History</label>
                                    <div className="space-y-3">
                                        {viewingStudent.academicInfo.gradeHistory.map((grade, index) => (
                                            <div key={index} className="p-4 border border-gray-200 rounded-xl bg-gray-50">
                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-700">Grade</div>
                                                        <div className="text-sm text-gray-900">{grade.grade}</div>
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-700">Academic Year</div>
                                                        <div className="text-sm text-gray-900">{grade.academicYear}</div>
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-700">Status</div>
                                                        <div className="text-sm text-gray-900 capitalize">{grade.status}</div>
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-700">Percentage</div>
                                                        <div className="text-sm text-gray-900">{grade.marks.percentage}%</div>
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-700">Marks</div>
                                                        <div className="text-sm text-gray-900">{grade.marks.obtainedMarks}/{grade.marks.totalMarks}</div>
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-700">Added Date</div>
                                                        <div className="text-sm text-gray-900">{new Date(grade.addedDate).toLocaleDateString()}</div>
                                                    </div>
                                                </div>
                                                {grade.remarks && (
                                                    <div className="mt-3">
                                                        <div className="text-sm font-medium text-gray-700">Remarks</div>
                                                        <div className="text-sm text-gray-900">{grade.remarks}</div>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </Card>

                        {/* Parent Information */}
                        <Card className="p-6">
                            <h3 className="text-xl font-semibold text-gray-900 mb-6">Parent Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Father's Name</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStudent.parentInfo.fatherName}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Mother's Name</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStudent.parentInfo.motherName}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Father's Phone</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStudent.parentInfo.fatherPhone}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Father's Email</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStudent.parentInfo.fatherEmail}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Father's Occupation</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStudent.parentInfo.fatherOccupation}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Father's Work Place</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStudent.parentInfo.fatherWorkPlace}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Mother's Occupation</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStudent.parentInfo.motherOccupation}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Mother's Work Place</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStudent.parentInfo.motherWorkPlace}
                                    </div>
                                </div>
                            </div>
                        </Card>

                        {/* Address Information */}
                        <Card className="p-6">
                            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                                <MapPin className="h-6 w-6 mr-3 text-emerald-600" />
                                Address Information
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Street Address</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStudent.address.street}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStudent.address.city}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStudent.address.state}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">ZIP Code</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStudent.address.zipCode}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStudent.address.country}
                                    </div>
                                </div>
                            </div>
                        </Card>

                        {/* Emergency Contact */}
                        <Card className="p-6">
                            <h3 className="text-xl font-semibold text-gray-900 mb-6">Emergency Contact</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Contact Name</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStudent.emergencyContact.name}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Relationship</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStudent.emergencyContact.relationship}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStudent.emergencyContact.phone}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStudent.emergencyContact.email || 'Not provided'}
                                    </div>
                                </div>
                            </div>
                        </Card>

                        {/* Modal Actions */}
                        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                            <Button
                                variant="secondary"
                                onClick={() => setViewingStudent(null)}
                            >
                                Close
                            </Button>
                            <Button
                                onClick={() => {
                                    setViewingStudent(null);
                                    onEdit(viewingStudent);
                                }}
                                className="flex items-center space-x-2"
                            >
                                <Edit className="h-4 w-4" />
                                <span>Edit Student</span>
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Image Modal */}
            {showImageModal && (
                <Modal
                    isOpen={true}
                    onClose={() => setShowImageModal(false)}
                    title={selectedImageAlt}
                    size="md"
                >
                    <div className="space-y-4">
                        <div className="flex justify-center">
                            <img
                                src={selectedImageUrl}
                                alt={selectedImageAlt}
                                className="max-w-full max-h-96 rounded-lg shadow-lg"
                            />
                        </div>
                        <div className="text-center">
                            <Button
                                variant="secondary"
                                onClick={() => setShowImageModal(false)}
                            >
                                Close
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default StudentView;
